/**
 * OnboardingGate
 *
 * Placed inside AppContent alongside BootstrapGate and WelcomeModal.
 * Decides whether to render the wizard overlay.
 *
 * Auto-trigger condition:
 *   1. canManageRolesPermissions() === true
 *   2. GET /api/companies returns an empty array (zero real companies)
 *   3. Neither hasDismissed nor hasCompleted
 *
 * Renders via ReactDOM.createPortal targeting document.body.
 * Wraps WizardShell in CompanyProvider with employeeId from useTimesheet().
 */

import { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { usePermissions } from '@/contexts/PermissionContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { CompanyProvider } from '@/features/multi-company';
import { WizardShell } from '@/components/onboarding/WizardShell';
import { apiClient } from '@/services/apiClient';
import type { CompanyListItem } from '@/types';

export function OnboardingGate() {
  const { canManageRolesPermissions, isLoading: permissionsLoading } = usePermissions();
  const { hasDismissed, hasCompleted, isWizardOpen, openWizard } = useOnboarding();
  const { currentEmployee } = useTimesheet();

  const [companiesChecked, setCompaniesChecked] = useState(false);
  const [hasZeroCompanies, setHasZeroCompanies] = useState(false);
  const hasAutoTriggered = useRef(false);

  // Check companies only when permissions are loaded and user is admin
  useEffect(() => {
    if (permissionsLoading) return;
    if (!canManageRolesPermissions()) return;
    if (hasDismissed || hasCompleted) {
      setCompaniesChecked(true);
      return;
    }

    let cancelled = false;

    async function checkCompanies() {
      try {
        const companies = await apiClient.get<CompanyListItem[]>('/companies');
        // Filter out DEFAULT company placeholder
        const real = companies.filter((c: any) => c.code !== 'DEFAULT');
        if (!cancelled) {
          setHasZeroCompanies(real.length === 0);
          setCompaniesChecked(true);
        }
      } catch {
        // On error, don't auto-trigger
        if (!cancelled) {
          setHasZeroCompanies(false);
          setCompaniesChecked(true);
        }
      }
    }

    checkCompanies();
    return () => {
      cancelled = true;
    };
  }, [permissionsLoading, canManageRolesPermissions, hasDismissed, hasCompleted]);

  // Auto-trigger the wizard
  useEffect(() => {
    if (!companiesChecked) return;
    if (hasDismissed || hasCompleted) return;
    if (!canManageRolesPermissions()) return;
    if (!hasZeroCompanies) return;
    if (hasAutoTriggered.current) return;

    hasAutoTriggered.current = true;
    openWizard();
  }, [
    companiesChecked,
    hasZeroCompanies,
    hasDismissed,
    hasCompleted,
    canManageRolesPermissions,
    openWizard,
  ]);

  // Render nothing while loading or conditions not met
  if (permissionsLoading) return null;
  if (!companiesChecked && !isWizardOpen) return null;
  if (!isWizardOpen) return null;
  if (!canManageRolesPermissions()) return null;
  if (!currentEmployee) return null;

  // Render wizard via portal, wrapping in CompanyProvider
  return ReactDOM.createPortal(
    <CompanyProvider employeeId={String(currentEmployee.id)}>
      <WizardShell />
    </CompanyProvider>,
    document.body,
  );
}
